import { useCallback, useState } from "react";
import { errorMessage } from "@/lib/api/errors";

interface AsyncActionState {
  readonly busy: boolean;
  readonly error: string | null;
}

/**
 * One in-flight action per screen, with its failure kept where the person is
 * looking. A second tap while busy is ignored rather than queued: a code
 * verified twice is a code that fails once.
 */
export function useAsyncAction(fallbackMessage: string) {
  const [state, setState] = useState<AsyncActionState>({ busy: false, error: null });

  const run = useCallback(
    async (action: () => Promise<void>): Promise<boolean> => {
      if (state.busy) return false;
      setState({ busy: true, error: null });
      try {
        await action();
        setState({ busy: false, error: null });
        return true;
      } catch (error) {
        const message = errorMessage(error);
        setState({ busy: false, error: message || fallbackMessage });
        return false;
      }
    },
    [state.busy, fallbackMessage],
  );

  const clearError = useCallback(() => setState((s) => ({ ...s, error: null })), []);

  return { ...state, run, clearError };
}
